import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'

import type {
	LogDashboardStateType,
	LogSummaryType,
	LogEntryType,
	LogSettingType,
	ApiGetDataStatus,
} from '@redux/types'

const today = new Date().toISOString().split('T')[0]

const initialState: LogDashboardStateType = {
	// dashboard
	summaryDate: today,
	summary: null,
	summaryStatus: 'init',

	// list
	listDate: today,
	listLevel: '',
	listCountry: '',
	listMethod: '',
	listUrl: '',
	listPage: 1,
	listLimit: 50,
	logs: [],
	logsTotalCount: 0,
	logsStatus: 'init',

	// trace
	traceRequestId: '',
	traces: [],
	tracesStatus: 'init',
	traceModalOpen: false,

	// detail modal
	detailModalOpen: false,
	detailModalLog: null,

	// settings
	logSetting: null,
	logSettingStatus: 'init',

	// triggers
	triggerGetSummary: false,
	triggerGetLogList: false,
	triggerGetTrace: false,
	triggerGetLogSetting: false,
	triggerUpdateLogSetting: false,
}

const slice = createSlice({
	name: 'logDashboard',
	initialState,
	reducers: {
		// dashboard
		SUMMARY_DATE: (state: LogDashboardStateType, action: PayloadAction<string>) => {
			state.summaryDate = action.payload
		},
		SUMMARY: (
			state: LogDashboardStateType,
			action: PayloadAction<LogSummaryType | null>,
		) => {
			state.summary = action.payload
		},
		SUMMARY_STATUS: (
			state: LogDashboardStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.summaryStatus = action.payload
		},

		// list
		LIST_DATE: (state: LogDashboardStateType, action: PayloadAction<string>) => {
			state.listDate = action.payload
		},
		LIST_LEVEL: (state: LogDashboardStateType, action: PayloadAction<string>) => {
			state.listLevel = action.payload
		},
		LIST_COUNTRY: (state: LogDashboardStateType, action: PayloadAction<string>) => {
			state.listCountry = action.payload
		},
		LIST_METHOD: (state: LogDashboardStateType, action: PayloadAction<string>) => {
			state.listMethod = action.payload
		},
		LIST_URL: (state: LogDashboardStateType, action: PayloadAction<string>) => {
			state.listUrl = action.payload
		},
		LIST_PAGE: (state: LogDashboardStateType, action: PayloadAction<number>) => {
			state.listPage = action.payload
		},
		LIST_LIMIT: (state: LogDashboardStateType, action: PayloadAction<number>) => {
			state.listLimit = action.payload
		},
		LOGS: (state: LogDashboardStateType, action: PayloadAction<LogEntryType[]>) => {
			state.logs = action.payload
		},
		LOGS_TOTAL_COUNT: (state: LogDashboardStateType, action: PayloadAction<number>) => {
			state.logsTotalCount = action.payload
		},
		LOGS_STATUS: (
			state: LogDashboardStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.logsStatus = action.payload
		},

		// trace
		TRACE_REQUEST_ID: (state: LogDashboardStateType, action: PayloadAction<string>) => {
			state.traceRequestId = action.payload
		},
		TRACES: (state: LogDashboardStateType, action: PayloadAction<LogEntryType[]>) => {
			state.traces = action.payload
		},
		TRACES_STATUS: (
			state: LogDashboardStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.tracesStatus = action.payload
		},
		TRACE_MODAL_OPEN: (state: LogDashboardStateType, action: PayloadAction<boolean>) => {
			state.traceModalOpen = action.payload
		},

		// detail modal
		DETAIL_MODAL_OPEN: (state: LogDashboardStateType, action: PayloadAction<boolean>) => {
			state.detailModalOpen = action.payload
		},
		DETAIL_MODAL_LOG: (
			state: LogDashboardStateType,
			action: PayloadAction<LogEntryType | null>,
		) => {
			state.detailModalLog = action.payload
		},

		// settings
		LOG_SETTING: (
			state: LogDashboardStateType,
			action: PayloadAction<LogSettingType | null>,
		) => {
			state.logSetting = action.payload
		},
		LOG_SETTING_STATUS: (
			state: LogDashboardStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.logSettingStatus = action.payload
		},

		// triggers
		TRIGGER_GET_SUMMARY: (
			state: LogDashboardStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerGetSummary = action.payload
		},
		TRIGGER_GET_LOG_LIST: (
			state: LogDashboardStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerGetLogList = action.payload
		},
		TRIGGER_GET_TRACE: (state: LogDashboardStateType, action: PayloadAction<boolean>) => {
			state.triggerGetTrace = action.payload
		},
		TRIGGER_GET_LOG_SETTING: (
			state: LogDashboardStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerGetLogSetting = action.payload
		},
		TRIGGER_UPDATE_LOG_SETTING: (
			state: LogDashboardStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerUpdateLogSetting = action.payload
		},
	},

	extraReducers: builder => {
		builder.addCase(PURGE, () => initialState)
	},
})

export const logDashboardActions = slice.actions
export default slice.reducer
